import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Link as RouterLink, useParams, useHistory } from 'react-router-dom'
import { UserContext } from '../../providers'
import {
  Box,
  Button,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
  Flex,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Center,
} from '@chakra-ui/react'
import { ChevronRightIcon } from '@chakra-ui/icons'
import { useTable } from 'react-table'
import { MainContentHeadingBox } from '../Base/MainContent'
import PermissionGuard from '../../components/PermissionGuard'
import { PermissionsEnum } from '../../../utils'
import { useTranslation } from 'react-i18next'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import { checkPermission } from '../../../utils/permissions'
import moment from 'moment'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import UrlEndpointsTextField from '../../components/UrlEndpoints/TextInputField'
import CodeEditor from '../../components/PathPolicy/CodeEditor'
import NotFoundView from '../../components/MediaLibrary/View/NotFoundView'
import useFetch, { CachePolicies } from 'use-http'

const PLACEHOLDER_HANDLER = `function handler(url, urlPrefix, context) {
  return { url };
}`

const formatDate = (timestamp?: number) =>
  timestamp ? moment(timestamp).format('MMM D, YYYY [at] hh:mm a') : '—'

const PublishDescriptionSchema = Yup.object({
  description: Yup.string()
    .trim()
    .required('description is required')
    .max(1000, 'description must be less than 1000 characters'),
})

const UrlEndpointFunctionTitleLayout: React.FC = () => {
  const { title } = useParams<{ title: string }>()
  const toast = useToast()
  const { user } = useContext(UserContext)
  const isSuperAdmin =
    checkPermission({
      user,
      permission: PermissionsEnum.CLIENT_MANAGE,
    }) || window.user?.isSimulation
  const { t } = useTranslation()

  const history = useHistory()

  const { response, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const [draftDetails, setDraftDetails] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [codeValue, setCodeValue] = useState('')
  const [codeActionLoading, setCodeActionLoading] = useState<
    'save' | 'publish' | null
  >(null)
  const [versions, setVersions] = useState<any[]>([])
  const [isVersionsLoading, setIsVersionsLoading] = useState(false)
  const [tabIndex, setTabIndex] = useState(0)
  const [hasLoadedVersions, setHasLoadedVersions] = useState(false)
  const {
    isOpen: isPublishModalOpen,
    onOpen: openPublishModal,
    onClose: closePublishModal,
  } = useDisclosure()

  const fetchDraftDetails = useCallback(async () => {
    if (!title || !user?.clientNumber) return
    setIsLoading(true)
    try {
      const data = await request.get(
        `api/clients/${
          user?.clientNumber
        }/url-endpoint-functions/${encodeURIComponent(title)}/draft`,
      )
      if (request.error) {
        throw data
      }
      setDraftDetails(data)
    } catch (error: any) {
      toast({
        description: error?.message || 'Unable to fetch draft details.',
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } finally {
      setIsLoading(false)
    }
  }, [title, user?.clientNumber])

  useEffect(() => {
    fetchDraftDetails()
  }, [fetchDraftDetails])

  const fetchVersions = useCallback(async () => {
    if (!title || !user?.clientNumber) return
    setIsVersionsLoading(true)
    try {
      let data = await request.get(
        `api/clients/${
          user?.clientNumber
        }/url-endpoint-functions/${encodeURIComponent(title)}`,
      )
      if (request.error) {
        throw data
      }

      data = data.filter(item => item.status === 'Published')
      setVersions(Array.isArray(data) ? data : [])
      setHasLoadedVersions(true)
    } catch (error: any) {
      setVersions([])
      toast({
        description:
          error?.message ||
          t('unable_to_fetch_versions', 'Unable to fetch versions.'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } finally {
      setIsVersionsLoading(false)
    }
  }, [title, user?.clientNumber, toast, t])

  useEffect(() => {
    setCodeValue(draftDetails?.code ?? '')
  }, [draftDetails?.code])

  useEffect(() => {
    // reset versions state when title/client changes
    setVersions([])
    setHasLoadedVersions(false)
    setTabIndex(0)
  }, [title, user?.clientNumber])

  const detailItems = useMemo(() => {
    const items = [
      ...(draftDetails?.latestPublishedVersion
        ? [
            {
              key: t('latest_version', 'Latest version'),
              value: (
                <Button
                  as={RouterLink}
                  variant="link"
                  cursor="pointer"
                  color="appClearBlue"
                  ml={0}
                  pl={0}
                  minW="unset"
                  to={`/settings/url-endpoint-functions/${encodeURIComponent(
                    title ?? '',
                  )}/${draftDetails?.latestPublishedVersion}`}
                >
                  {draftDetails?.latestPublishedVersion}
                </Button>
              ),
            },
            {
              key: t('last_published_date', 'Published date'),
              value: (
                <Text color={colors.appDarkIndigo} fontWeight="semibold">
                  {draftDetails?.updatedAt
                    ? formatDate(draftDetails.updatedAt)
                    : ''}
                </Text>
              ),
            },
          ]
        : []),
      ...(!isSuperAdmin
        ? [
            {
              key: t('description', 'Description'),
              value: (
                <Box
                  display="block"
                  maxH="150px"
                  bg="white"
                  color={colors.appDarkIndigo}
                  fontWeight="semibold"
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                  overflowY="auto"
                >
                  {draftDetails?.description}
                </Box>
              ),
            },
          ]
        : []),
    ]

    return items
  }, [draftDetails, isSuperAdmin, title])

  const hasUnsavedCodeChanges = codeValue !== (draftDetails?.code ?? '')

  const handleSaveCode = async () => {
    if (!title) return

    // simple guard: block save if code editor is empty
    if (!codeValue || !codeValue.trim()) {
      toast({
        description: t('code_is_required', 'Code is empty.'),
        status: 'error',
        isClosable: true,
        duration: 4000,
        position: 'top-right',
        variant: 'outline',
      })
      return
    }

    setCodeActionLoading('save')
    try {
      const data = await request.patch(
        `/api/clients/${
          user?.clientNumber
        }/url-endpoint-functions/${encodeURIComponent(title)}`,
        { code: codeValue },
      )
      if (request.error) {
        throw data
      }
      setDraftDetails((prev: any) => ({ ...prev, code: codeValue }))
      toast({
        description: t('code_saved', 'Code saved'),
        status: 'success',
        isClosable: true,
        duration: 4000,
        position: 'top-right',
        variant: 'outline',
      })
    } catch (error: any) {
      toast({
        description:
          error?.message || t('something_went_wrong', 'Something went wrong!'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } finally {
      setCodeActionLoading(null)
    }
  }

  const handlePublishCode = async (description: string) => {
    if (!title) return
    setCodeActionLoading('publish')
    try {
      const data = await request.patch(
        `/api/clients/${
          user?.clientNumber
        }/url-endpoint-functions/${encodeURIComponent(title)}`,
        { code: codeValue, description, status: 'Published' },
      )
      if (request.error) {
        throw data
      }

      if (data?.version) {
        history.push(
          `/settings/url-endpoint-functions/${encodeURIComponent(
            title ?? '',
          )}/${data.version}`,
        )
      } else {
        await fetchDraftDetails()
      }

      toast({
        description: t('function_published', 'Function published'),
        status: 'success',
        isClosable: true,
        duration: 4000,
        position: 'top-right',
        variant: 'outline',
      })
    } catch (error: any) {
      toast({
        description:
          error?.message || t('something_went_wrong', 'Something went wrong!'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } finally {
      setCodeActionLoading(null)
    }
  }

  const versionColumns = useMemo(
    () => [
      {
        Header: t('version_number', 'Version no.'),
        accessor: 'version',
        Cell: ({ value }: { value: number }) => (
          <Button
            as={RouterLink}
            variant="link"
            color="appClearBlue"
            to={`/settings/url-endpoint-functions/${encodeURIComponent(
              title ?? '',
            )}/${value}`}
          >
            {value}
          </Button>
        ),
      },
      {
        Header: t('published_date', 'Published date'),
        accessor: 'updatedAt',
        Cell: ({ value }: { value?: number }) => (
          <Box maxWidth="200px" minWidth={'150px'}>
            {value ? formatDate(value) : '—'}
          </Box>
        ),
      },
      {
        Header: t('description', 'Description'),
        accessor: 'description',
        Cell: ({ value }: { value: string }) => (
          <Box
            minWidth="400px"
            maxWidth="500px"
            maxH="100px"
            overflowY="auto"
            whiteSpace="normal"
            wordBreak="break-all"
            lineHeight="1.5"
          >
            <Text>{value || ''}</Text>
          </Box>
        ),
      },
    ],
    [title],
  )
  const {
    getTableProps: getVersionTableProps,
    getTableBodyProps: getVersionTableBodyProps,
    headerGroups: versionHeaderGroups,
    rows: versionRows,
    prepareRow: prepareVersionRow,
  } = useTable({ columns: versionColumns, data: versions })

  const handleTabChange = (index: number) => {
    setTabIndex(index)
    if (index === 1 && !hasLoadedVersions) {
      fetchVersions()
    }
  }

  if (isLoading) {
    return (
      <>
        <MainContentHeadingBox></MainContentHeadingBox>
        <Center padding="16px" height="100%">
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="appPaleGrey"
            color="appBlue"
            size="lg"
          />
        </Center>
      </>
    )
  }
  if (!draftDetails) {
    return <NotFoundView />
  }
  return (
    <>
      <MainContentHeadingBox></MainContentHeadingBox>
      <Box padding="16px">
        <Breadcrumb
          spacing="8px"
          color="#3B485E"
          fontWeight="semibold"
          separator={<ChevronRightIcon color="appSlate" />}
        >
          <BreadcrumbItem>
            <BreadcrumbLink
              as={RouterLink}
              to="/settings/url-endpoint-functions"
              _focus={{
                outline: 'none',
              }}
            >
              {t('functions', 'Functions')}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink
              _focus={{
                outline: 'none',
              }}
              as={RouterLink}
              to={`/settings/url-endpoint-functions/${encodeURIComponent(
                title ?? '',
              )}`}
            >
              {title}
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
        {detailItems?.length > 0 && (
          <VStack align="stretch" spacing={'8px'} mt={6}>
            {detailItems.map(item => (
              <Flex key={item.key} direction="row" gap={2}>
                <Box>
                  <Text color={colors.appSlate}>{`${item.key}: `}</Text>
                </Box>
                {item.value}
              </Flex>
            ))}
          </VStack>
        )}
        {draftDetails && isSuperAdmin && (
          <Formik
            enableReinitialize
            initialValues={{ description: draftDetails?.description ?? '' }}
            validationSchema={PublishDescriptionSchema}
            validateOnMount
            onSubmit={async (values, actions) => {
              try {
                const data = await request.patch(
                  `/api/clients/${
                    user?.clientNumber
                  }/url-endpoint-functions/${encodeURIComponent(title)}`,
                  {
                    description: values.description,
                  },
                )
                if (request.error) {
                  throw data
                }
                toast({
                  description: t(
                    'description_updated_successfully',
                    'Description updated successfully',
                  ),
                  status: 'success',
                  isClosable: true,
                  duration: 4000,
                  position: 'top-right',
                  variant: 'outline',
                })
                setDraftDetails((prev: any) => ({
                  ...prev,
                  description: values.description,
                }))
              } catch (error: any) {
                toast({
                  description:
                    error?.message ||
                    t('something_went_wrong', 'Something went wrong!'),
                  status: 'error',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
                actions.resetForm()
              } finally {
                actions.setSubmitting(false)
              }
            }}
          >
            {({
              values,
              handleChange,
              dirty,
              isSubmitting,
              isValid,
              errors,
              touched,
            }) => (
              <Form>
                <Flex
                  mt={'8px'}
                  gap={4}
                  align={['stretch', 'flex-end']}
                  direction={['column', 'row']}
                >
                  <Box flex="1">
                    <UrlEndpointsTextField
                      name="description"
                      placeholder={t('enter_description', 'Enter description')}
                      label={t('description', 'Description')}
                      description=""
                      value={values.description}
                      handleChange={handleChange}
                      required
                      isDisabled={isSubmitting}
                    />
                  </Box>
                  <Button
                    minW="120px"
                    type="submit"
                    isDisabled={!dirty || !isValid}
                    isLoading={isSubmitting}
                  >
                    {t('save', 'Save')}
                  </Button>
                </Flex>
                {touched.description && errors.description && (
                  <Text mt={2} color="red.500" fontSize="sm">
                    {errors.description}
                  </Text>
                )}
              </Form>
            )}
          </Formik>
        )}
        <Tabs size="md" mt={8} index={tabIndex} onChange={handleTabChange}>
          <TabList columnGap="16px">
            <Tab
              borderBottom="2px solid transparent"
              _selected={{
                color: 'appBlue',
                borderColor: 'appBlue',
                bg: '#f0f5fd',
                fontWeight: 600,
              }}
              _focus={{ outline: 'none' }}
            >
              {t('draft_code_tab', 'Draft code')}
            </Tab>
            <Tab
              borderBottom="2px solid transparent"
              _selected={{
                color: 'appBlue',
                borderColor: 'appBlue',
                bg: '#f0f5fd',
                fontWeight: 600,
              }}
              _focus={{ outline: 'none' }}
            >
              {t('versions_tab', 'Versions')}
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel p={0} mt="0px" overflowY="auto">
              <CodeEditor
                value={codeValue}
                placeholder={PLACEHOLDER_HANDLER}
                onChange={val => setCodeValue(val ?? '')}
                readOnly={!isSuperAdmin}
                height="300px"
              />
              {isSuperAdmin && (
                <Flex mt={4} gap={3} justify="flex-end" wrap="wrap">
                  <Button
                    onClick={handleSaveCode}
                    isDisabled={!hasUnsavedCodeChanges}
                    variant="outline"
                    isLoading={codeActionLoading === 'save'}
                  >
                    {t('save', 'Save')}
                  </Button>
                  <Button onClick={openPublishModal}>
                    {t('publish', 'Publish')}
                  </Button>
                </Flex>
              )}
            </TabPanel>
            <TabPanel p={0} mt="1.5rem" height="400px" overflowY="auto">
              {isVersionsLoading ? (
                <Center>
                  <Spinner size="md" />
                </Center>
              ) : (
                <Table {...getVersionTableProps()} size="md" padding="0px">
                  <Thead>
                    {versionHeaderGroups.map(headerGroup => {
                      const { key, ...rest } = headerGroup.getHeaderGroupProps()
                      return (
                        <Tr key={key ?? headerGroup.id} {...rest}>
                          {headerGroup.headers.map(column => {
                            const { key: headerKey, ...headerProps } =
                              column.getHeaderProps()
                            return (
                              <Th key={headerKey ?? column.id} {...headerProps}>
                                {column.render('Header')}
                              </Th>
                            )
                          })}
                        </Tr>
                      )
                    })}
                  </Thead>
                  <Tbody {...getVersionTableBodyProps()}>
                    {versions.length === 0 ? (
                      <Tr>
                        <Td
                          colSpan={versionColumns.length}
                          textAlign="center"
                          py={6}
                        >
                          {t(
                            'no_versions_available_2',
                            'No versions available',
                          )}
                        </Td>
                      </Tr>
                    ) : (
                      versionRows.map(row => {
                        prepareVersionRow(row)
                        const { key, ...rest } = row.getRowProps()
                        return (
                          <Tr key={key ?? row?.id} {...rest}>
                            {row.cells.map(cell => {
                              const { key, ...cellProps } = cell.getCellProps()
                              return (
                                <Td key={key ?? cell?.column.id} {...cellProps}>
                                  {cell.render('Cell')}
                                </Td>
                              )
                            })}
                          </Tr>
                        )
                      })
                    )}
                  </Tbody>
                </Table>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
      {isSuperAdmin && (
        <Formik
          enableReinitialize
          initialValues={{ description: draftDetails?.description ?? '' }}
          validationSchema={PublishDescriptionSchema}
          validateOnMount
          onSubmit={async (values, actions) => {
            try {
              // block publish if code editor is empty
              if (!codeValue || !codeValue.trim()) {
                toast({
                  description: t(
                    'code_is_required',
                    'Code is required to publish.',
                  ),
                  status: 'error',
                  isClosable: true,
                  duration: 4000,
                  position: 'top-right',
                  variant: 'outline',
                })
                return
              }
              await handlePublishCode(values.description)
              closePublishModal()
            } finally {
              actions.setSubmitting(false)
            }
          }}
        >
          {({
            values,
            handleChange,
            isSubmitting,
            resetForm,
            isValid,
            errors,
            touched,
          }) => {
            const handleModalClose = () => {
              resetForm()
              closePublishModal()
            }

            return (
              <Modal
                size="xl"
                isOpen={isPublishModalOpen}
                onClose={handleModalClose}
              >
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader>
                    {t('publish_function', 'Publish function')}
                  </ModalHeader>
                  <ModalCloseButton />
                  <Form>
                    <ModalBody minH="200px">
                      <UrlEndpointsTextField
                        name="description"
                        label={t('description', 'Description')}
                        placeholder={t(
                          'enter_description',
                          'Enter description',
                        )}
                        description="This description will be linked to this published version."
                        value={values.description}
                        handleChange={handleChange}
                        required
                        isDisabled={isSubmitting}
                      />
                      {touched.description && errors.description && (
                        <Text mt={2} color="red.500" fontSize="sm">
                          {errors.description}
                        </Text>
                      )}
                    </ModalBody>
                    <ModalFooter gap={3}>
                      <Button onClick={handleModalClose} variant="outline">
                        {t('cancel', 'Cancel')}
                      </Button>
                      <Button
                        type="submit"
                        colorScheme="appBlue"
                        isLoading={
                          isSubmitting || codeActionLoading === 'publish'
                        }
                        isDisabled={!isValid || isSubmitting}
                      >
                        {t('save', 'Save')}
                      </Button>
                    </ModalFooter>
                  </Form>
                </ModalContent>
              </Modal>
            )
          }}
        </Formik>
      )}
    </>
  )
}

export default UrlEndpointFunctionTitleLayout
